import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isFieldVisible, type DocumentDef } from '@project/contracts'
import { Button, Modal } from '../design/index.js'
import { useApi } from '../app/api.js'
import { navigate, paths, type Query } from '../app/router.js'
import { FormField } from './controls.js'
import { useSettings } from './queries.js'
import { ErrorBlock, Loading } from './feedback.js'

export interface CreateWizardProps {
  readonly def: DocumentDef
  readonly companyId: string
  readonly docRoute: string
  /** Values carried in from the URL, e.g. the parent document on Related Docs. */
  readonly seed: Query
  readonly onClose: () => void
}

/**
 * Creation always starts by capturing the few answers that decide what the form
 * looks like. The wizard is the document definition's `createWizard`, nothing
 * more — there is no per-document creation screen anywhere in this client.
 */
export const CreateWizard = ({ def, companyId, docRoute, seed, onClose }: CreateWizardProps) => {
  const api = useApi()
  const wizard = def.createWizard
  const settings = useSettings()

  /*
    What the document this was raised from already answers.

    "Create the next document" used to carry the source id in the URL and
    nothing on the server read it, so approving a purchase order and pressing
    "Receipt note (GRN)" opened an empty wizard in which the supplier and the
    order had to be found and picked again by hand. The server now says what
    the new document should start with — including the back-reference that puts
    its lines into the allocation ledger — and the wizard opens on that.
  */
  const from = seed['from']
  const fromType = seed['fromType']
  const carried = useQuery({
    queryKey: ['createSeed', api.companyId, def.key, from, fromType],
    queryFn: () => api.createSeed(def.key, from!, fromType!),
    enabled: Boolean(from && fromType),
    staleTime: 60_000,
  })

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {}
    for (const field of wizard.fields) {
      const seeded = seed[field.key]
      if (seeded !== undefined) initial[field.key] = seeded
      else if (field.defaultValue !== undefined) initial[field.key] = field.defaultValue
    }
    return initial
  })
  const [touched, setTouched] = useState(false)

  // Merged rather than replaced: anything the URL said explicitly stays put,
  // and the operator's own edits survive the answer arriving.
  useEffect(() => {
    if (!carried.data) return
    setValues((prev) => {
      const next = { ...prev }
      for (const [key, value] of Object.entries(carried.data.values)) {
        if (next[key] === undefined || next[key] === '') next[key] = value
      }
      return next
    })
  }, [carried.data])

  // A question the definition does not ask — of this order type, or of a
  // company with the feature switched off — is neither shown nor required.
  const asked = wizard.fields.filter((field) => isFieldVisible(field, values, settings.data?.values))
  const missing = asked.filter((f) => f.required && !String(values[f.key] ?? '').trim()).map((f) => f.key)

  const create = useMutation({
    mutationFn: () => {
      const fields: Record<string, unknown> = { ...values }
      if (from) fields['fromDocumentId'] = from
      if (fromType) fields['fromDocumentType'] = fromType
      if (seed['clone']) fields['cloneOfDocumentId'] = seed['clone']
      return api.create(def.key, fields)
    },
    onSuccess: (doc) => navigate(paths.document(companyId, docRoute, doc.id)),
  })

  const waiting = carried.isLoading

  return (
    <Modal
      title={wizard.title}
      subtitle={wizard.subtitle}
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button
            kind="primary"
            disabled={create.isPending || waiting}
            onClick={() => {
              setTouched(true)
              if (missing.length === 0) create.mutate()
            }}
          >
            {create.isPending ? 'Creating…' : 'Done'}
          </Button>
        </>
      }
    >
      {create.error ? <ErrorBlock error={create.error} /> : null}

      {waiting ? <Loading /> : (
        <div className="stack" style={{ marginTop: create.error ? 20 : 0 }}>
          {asked.map((field) => (
            <FormField
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              valueLabel={carried.data?.labels[field.key]}
              onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
              error={touched && missing.includes(field.key) ? `${field.label} is required.` : undefined}
            />
          ))}
        </div>
      )}
    </Modal>
  )
}
